import React, { useEffect, useState } from 'react';
import { useUserData } from '../contexts/UserDataContext';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

interface CompletedWord {
    _id?: string;
    word: string;
    translation: string;
    meaning?: string;
    difficulty?: string;
    [key: string]: any;
}

const difficultyOptions = [
    { key: 'low', label: 'Low' },
    { key: 'medium', label: 'Medium' },
    { key: 'high', label: 'High' },
];

export default function CompletedWordsCard() {
    const { userData } = useUserData();
    const { currentUser } = useAuth();
    const selectedLanguage = userData?.selectedLanguage || 'en';
    console.log('CompletedWordsCard - userData:', userData);
    console.log('CompletedWordsCard - currentUser:', currentUser);
    console.log('CompletedWordsCard - selectedLanguage:', selectedLanguage);
    const [selectedDifficulty, setSelectedDifficulty] = useState<string>('low');
    const [words, setWords] = useState<CompletedWord[]>([]);
    const [loading, setLoading] = useState(false);
    const [userCount, setUserCount] = useState<number>(0);
    const PAGE_SIZE = 5;
    const [page, setPage] = useState(0);

    useEffect(() => {
        if (!currentUser?.uid) return;
        console.log('Fetching completed words for user:', currentUser.uid, 'difficulty:', selectedDifficulty);
        setLoading(true);
        api.getCompletedWords(currentUser.uid, selectedDifficulty)
            .then(res => {
                console.log('Completed words response:', res);
                setWords(res || []);
                setPage(0);
            })
            .catch(error => {
                console.error('Error fetching completed words:', error);
            })
            .finally(() => setLoading(false));

        // Remove the unnecessary count API call
        // api.getCompletedWordsCount(currentUser.uid, selectedDifficulty)
        //     .then(count => {
        //         setUserCount(count);
        //     })
        //     .catch(err => {
        //         console.error('Error fetching completed words count:', err);
        //         setUserCount(0);
        //     });
    }, [selectedDifficulty, currentUser?.uid]);

    if (loading) {
        return <div className="text-center text-lg text-blue-500 font-semibold py-12">Loading...</div>;
    }
    const maxPage = Math.max(0, Math.ceil(words.length / PAGE_SIZE) - 1);
    const pageWords = words.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

    return (
        <div className="bg-white/70 backdrop-blur-lg border border-blue-200 rounded-2xl p-8 shadow-xl max-h-[80vh] overflow-y-auto">
            <div className="max-w-3xl mx-auto">
                <h1 className="text-4xl font-extrabold tracking-tight drop-shadow-lg mb-6 text-center">
                    <span className="bg-gradient-to-r from-blue-500 via-green-500 to-pink-400 bg-clip-text text-transparent">Completed Words</span>
                </h1>
            </div>
            <div className="flex flex-wrap gap-2 justify-center mb-6">
                {difficultyOptions.map((opt, idx) => (
                    <button
                        key={opt.key}
                        className={`px-6 py-2 rounded-full font-semibold text-base transition-all duration-200 shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-300 ${selectedDifficulty === opt.key ? 'bg-gradient-to-r from-blue-500 via-green-500 to-pink-500 text-white scale-105' : 'bg-blue-100 text-blue-800 hover:bg-gradient-to-r hover:from-blue-200 hover:via-green-100 hover:to-pink-100 hover:text-blue-900'}`}
                        onClick={() => setSelectedDifficulty(opt.key)}
                    >
                        {idx === 0 ? 'Beginner Level' : idx === 1 ? 'Intermediate Level' : 'Advanced Level'}
                    </button>
                ))}
            </div>
            <div className="text-lg font-bold text-blue-700 text-center mb-4">Total: {words.length}</div>
            {words.length === 0 ? (
                <div className="bg-white/70 backdrop-blur-lg border border-blue-200 rounded-2xl p-8 shadow-xl">
                    <div className="text-center">
                        <div className="text-4xl mb-4">✅</div>
                        <p className="text-gray-600">No completed words yet. Mark words as done to see them here! (Total: {userCount})</p>
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    {pageWords.map((w, idx) => (
                        <div key={w._id || w.word + idx} className="bg-white/70 rounded-2xl p-4 shadow-md border border-blue-100 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                            <div>
                                <div className="text-xl font-bold text-gray-900 mb-1">
                                    {w[userData?.selectedLanguage || 'en'] || w.word || <span className='italic text-red-500'>Word not found (check itemId format in completed collection)</span>}
                                </div>
                                {userData?.selectedLanguage !== 'en' && w.word && (
                                    <div className="text-base text-gray-500">{w.word}</div>
                                )}
                                {w.meaning && (
                                    <div className="text-base text-gray-500 italic">{w.meaning}</div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {/* Pagination controls */}
            <div className="flex justify-center gap-4 mt-4">
                <button
                    className="px-6 py-2 rounded-full bg-gradient-to-r from-gray-200 to-gray-400 text-gray-800 font-semibold shadow-lg hover:from-gray-300 hover:to-gray-500 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                >
                    Previous
                </button>
                <span className="px-4 py-2 text-gray-700 font-semibold">Page {page + 1} of {maxPage + 1}</span>
                <button
                    className="px-6 py-2 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 text-white font-semibold shadow-lg hover:from-blue-500 hover:to-blue-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:opacity-50"
                    onClick={() => setPage(p => (p + 1 < maxPage + 1 ? p + 1 : p))}
                    disabled={page === maxPage}
                >
                    Next
                </button>
            </div>
        </div>
    );
} 